import type {
  ApolloClient,
  ApolloQueryResult,
  ObservableQuery,
  ObservableSubscription,
} from '@apollo/client';
import {
  GetFolderAssets,
  type GetFolderAssetsQuery,
  type GetFolderAssetsQueryVariables,
} from '@frameio/assets/shared/gql/generated/GetFolderAssets';
import {
  type FolderAssetsConnection,
  getFolderAssetsConnection,
} from '@frameio/assets/shared/utils/getFolderAssetsConnection';
import {
  GetAncestorFolderIds,
  type GetAncestorFolderIdsQuery,
} from '../gql/generated/GetAncestorFolderIds';

type FolderQuery = ObservableQuery<
  GetFolderAssetsQuery,
  GetFolderAssetsQueryVariables
>;

/**
 * The FolderQueryInfo class maintains data used by the useProjectFolders hook
 * to run queries and subscribe to changes to the cache for a specific folder'
 * child folders.
 */
export class FolderQueryInfo {
  private id = '';

  private accountId = '';

  private expanded = true;

  private loading = false;

  /**
   * The ObservableQuery instance used to subscribe to cache changes and
   * fetch more folders for paginationg.
   * */
  public watchQuery: FolderQuery | null = null;

  /** The ObservableSubscription instance that listens to cache changes. */
  public subscription: ObservableSubscription | null = null;

  /** The results of the folderAssets query for this folder. */
  public folderChildren: FolderAssetsConnection | null = null;

  /** The id of the last child folder for the currently fetched page(s). */
  private boundaryId: string | null = null;

  constructor(id: string, accountId: string) {
    this.id = id;
    this.accountId = accountId;
  }

  /**
   * startWatching creates an ObservableQuery and subscribes to it for this
   * folder. Whenever the subscription is fired, we update the folderChildren
   * and set the cursorTarget if necessary, then we fire callback() once the
   * internal data has been updated.
   *
   * @param client The ApolloClient instance
   * @param callback Callback to fire when the cache has been updated for this folder
   * @returns subscription The ObservableSubscription instance, useful for cleanup
   */
  public startWatching(client: ApolloClient<any>, callback: () => void) {
    const watchQuery = client.watchQuery({
      query: GetFolderAssets,
      variables: {
        accountId: this.accountId,
        assetType: 'FOLDER',
        folderId: this.id,
        page: {
          first: PAGE_SIZE,
        },
      },
    });

    const subscription = watchQuery.subscribe((observer) => {
      const folderAssets = getFolderAssetsConnection(observer.data);
      if (!folderAssets) return;

      this.folderChildren = folderAssets;

      if (folderAssets.pageInfo.hasNextPage) {
        const length = folderAssets.nodes.length;

        this.boundaryId = folderAssets.nodes[length - 1]?.id ?? null;
      } else {
        this.boundaryId = null;
      }

      callback();
    });

    this.watchQuery = watchQuery;
    this.subscription = subscription;

    return subscription;
  }

  /**
   * Unsubscribe from the ObservableSubscription and null out the other
   * fields.
   */
  public stopWatching() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    this.expanded = false;
    this.boundaryId = null;
    this.watchQuery = null;
  }

  public get isLoading() {
    return this.loading;
  }

  public set isLoading(loading: boolean) {
    this.loading = loading;
  }

  public get isExpanded() {
    return this.expanded;
  }

  public set isExpanded(expanded: boolean) {
    this.expanded = expanded;
  }

  /**
   * Checks if a given child folder id is a cursor boundary. In other words, does
   * the targetId match the id of the last child id in the current page? This is
   * useful for knowing when to fetch the next page of folders.
   *
   * @param targetId The id of a child folder
   * @returns isCursorBoundary Whether the target folder matches the last folder
   */
  public isCursorBoundary(targetId: string) {
    return this.boundaryId === targetId;
  }

  /**
   * Fetches the next page of folder children for this folder.
   */
  public fetchMore() {
    const after = this.folderChildren?.pageInfo.endCursor;

    if (!after) return;

    this.watchQuery?.fetchMore({
      variables: {
        page: {
          first: PAGE_SIZE,
          after,
        },
      },
    });
  }

  /**
   * Expands each ancestor of the current folder and fetches children until the
   * current folder is found, or the limits are reached. The limits attempt to
   * prevent overfetching and rate limiting.
   *
   * @param client The ApolloClient instance
   * @param trackFolder Callback fired when a new ancestor folder is seen
   * @param doneLoading callback fired when all fetching is complete
   */
  public async fetchAncestors(
    client: ApolloClient<any>,
    trackFolder: (id: string) => void,
    doneLoading: () => void
  ) {
    let ancestors: ApolloQueryResult<GetAncestorFolderIdsQuery>;
    try {
      ancestors = await client.query({
        query: GetAncestorFolderIds,
        variables: {
          id: this.id,
        },
      });
    } catch {
      doneLoading();
      return;
    }

    const asset = ancestors.data.account?.asset;
    const parentId = asset?.parent?.id;
    const ancestorFolders = asset?.ancestorFolders?.filter(
      (folder) => folder !== null && folder !== undefined
    );

    if (!ancestorFolders || !parentId) {
      doneLoading();
      return;
    }

    if (ancestorFolders?.length > ANCESTOR_DEPTH_LIMIT) {
      doneLoading();
      return;
    }

    const cachedParent = client.cache.readQuery({
      query: GetFolderAssets,
      variables: {
        accountId: this.accountId,
        assetType: 'FOLDER',
        folderId: parentId,
        page: {
          first: PAGE_SIZE,
        },
      },
    });
    const cachedParentFolderAssets = getFolderAssetsConnection(cachedParent);

    if (
      cachedParentFolderAssets?.nodes.find((asset) => asset?.id === this.id)
    ) {
      // Data is already cached — no need to re-fetch, but ancestors still
      // need to be expanded in the tree.
      ancestorFolders.forEach(({ id }) => trackFolder(id));
      doneLoading();
      return;
    }

    const promises = ancestorFolders.map(async ({ id }, index) => {
      const target =
        index === ancestorFolders.length - 1
          ? this.id
          : ancestorFolders[index + 1].id;

      trackFolder(id);

      const findTarget = async (runCount = 0, endCursor?: string) => {
        if (runCount >= ANCESTOR_BREADTH_LIMIT) return true;

        const cached = client.cache.readQuery({
          query: GetFolderAssets,
          variables: {
            accountId: this.accountId,
            assetType: 'FOLDER',
            folderId: id,
            page: {
              first: PAGE_SIZE,
            },
          },
        });
        const cachedFolderAssets = getFolderAssetsConnection(cached);

        const inCache =
          (cachedFolderAssets?.nodes ?? []).findIndex(
            (asset) => asset?.id === target
          ) !== -1;

        if (inCache) {
          return true;
        }

        const result = await client.query({
          fetchPolicy: 'network-only',
          query: GetFolderAssets,
          variables: {
            accountId: this.accountId,
            assetType: 'FOLDER',
            folderId: id,
            page: {
              after: endCursor,
              first: PAGE_SIZE,
            },
          },
        });
        const folderAssets = getFolderAssetsConnection(result.data);

        if (!folderAssets) return false;
        const found =
          folderAssets.nodes.findIndex((asset) => asset?.id === target) > -1;

        if (found) return true;
        if (!folderAssets.pageInfo.hasNextPage) return false;

        return findTarget(
          runCount + 1,
          folderAssets.pageInfo.endCursor ?? undefined
        );
      };

      return findTarget();
    });

    Promise.all(promises).finally(doneLoading);
  }
}

export const PAGE_SIZE = 200;
const ANCESTOR_DEPTH_LIMIT = 5;
const ANCESTOR_BREADTH_LIMIT = 5;
